import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Plus, Trash2, Loader2, ExternalLink, Paperclip, Upload, Link2, X, Pencil,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { apiClient } from '@/services/apiClient';
import type { OnboardingDocument, PlanTask } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

const DOC_TYPES = [
  { value: 'contract', label: 'Contract / Agreement', desc: 'MSA, BAA, NDA, service agreements' },
  { value: 'sow', label: 'SOW / Proposal', desc: 'Scope of work, proposals, quotes' },
  { value: 'config', label: 'Configuration', desc: 'Settings export, config screenshots, setup docs' },
  { value: 'screenshot', label: 'Screenshot / Image', desc: 'Screenshots, diagrams, photos' },
  { value: 'training', label: 'Training Material', desc: 'Training guides, video links, slide decks' },
  { value: 'checklist', label: 'Checklist / Form', desc: 'Completed checklists, intake forms, assessments' },
  { value: 'other', label: 'Other', desc: 'Any other document type' },
];

const docTypeColors: Record<string, string> = {
  contract: 'bg-blue-100 text-blue-700', sow: 'bg-purple-100 text-purple-700',
  config: 'bg-green-100 text-green-700', screenshot: 'bg-yellow-100 text-yellow-700',
  training: 'bg-indigo-100 text-indigo-700', checklist: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-700',
};

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.heic,.tif,.tiff,.csv,.txt,.zip';

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props { planId: number; tasks: PlanTask[]; }

export default function PlanDocumentsPanel({ planId, tasks }: Props) {
  const [docs, setDocs] = useState<OnboardingDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    document_name: '', document_type: 'other', description: '',
    file_url: '', task_id: '',
  });

  const [editingDoc, setEditingDoc] = useState<OnboardingDocument | null>(null);
  const [editForm, setEditForm] = useState({
    document_name: '', document_type: 'other', description: '',
    file_url: '', task_id: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (filterType) params.document_type = filterType;
      setDocs(await api.listPlanDocuments(planId, params));
    } catch (err) { console.error('Failed to load documents', err); }
    finally { setLoading(false); }
  }, [planId, filterType]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ document_name: '', document_type: 'other', description: '', file_url: '', task_id: '' });
    setSelectedFile(null);
    setUploadMode('file');
    setDragActive(false);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    if (!form.document_name) {
      setForm(f => ({ ...f, document_name: file.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleAdd = async () => {
    if (!form.document_name.trim()) return;
    setSaving(true);
    try {
      if (uploadMode === 'file' && selectedFile) {
        // Upload via multipart form
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('document_type', form.document_type);
        formData.append('document_name', form.document_name.trim());
        if (form.description.trim()) formData.append('description', form.description.trim());
        if (form.task_id) formData.append('task_id', form.task_id);
        await apiClient.post(
          `/api/platform/v1/support/onboarding/plans/${planId}/documents/file`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else if (uploadMode === 'url' && form.file_url.trim()) {
        // Create via URL
        await api.createPlanDocument(planId, {
          document_type: form.document_type,
          document_name: form.document_name.trim(),
          description: form.description.trim() || undefined,
          file_url: form.file_url.trim(),
          task_id: form.task_id ? parseInt(form.task_id) : undefined,
        });
      } else {
        alert('Please select a file or enter a URL');
        setSaving(false);
        return;
      }
      setShowAdd(false);
      resetForm();
      load();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this document?')) return;
    try { await api.deletePlanDocument(planId, id); load(); }
    catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const openEdit = (doc: OnboardingDocument) => {
    setEditingDoc(doc);
    setEditForm({
      document_name: doc.document_name || '',
      document_type: doc.document_type || 'other',
      description: doc.description || '',
      file_url: doc.file_url || '',
      task_id: doc.task_id ? String(doc.task_id) : '',
    });
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;
    if (!editForm.document_name.trim() || !editForm.file_url.trim()) return;
    setEditSaving(true);
    try {
      await api.updatePlanDocument(planId, editingDoc.id, {
        document_name: editForm.document_name.trim(),
        document_type: editForm.document_type,
        description: editForm.description.trim() || undefined,
        file_url: editForm.file_url.trim(),
        task_id: editForm.task_id ? parseInt(editForm.task_id) : null,
      });
      setEditingDoc(null);
      load();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setEditSaving(false); }
  };

  const canSave = form.document_name.trim() && (
    (uploadMode === 'file' && selectedFile) || (uploadMode === 'url' && form.file_url.trim())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">All types</SelectItem>
              {DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-400">{docs.length} document{docs.length !== 1 ? 's' : ''}</span>
        </div>
        <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }}><Plus className="h-3 w-3 mr-1" />Add Document</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : docs.length === 0 ? (
        <p className="text-center text-gray-400 py-6">No documents attached yet.</p>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 group">
              <div className="p-2 bg-gray-100 rounded-lg shrink-0"><FileText className="h-5 w-5 text-gray-500" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate">
                    {doc.document_name}
                  </a>
                  <Badge variant="outline" className={`text-xs ${docTypeColors[doc.document_type] || docTypeColors.other}`}>
                    {DOC_TYPES.find(d => d.value === doc.document_type)?.label || doc.document_type}
                  </Badge>
                  {doc.task_name && <Badge variant="secondary" className="text-xs"><Paperclip className="h-2.5 w-2.5 mr-0.5" />{doc.task_name}</Badge>}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                  {doc.uploaded_by_name && <span>by {doc.uploaded_by_name}</span>}
                  <span>{new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}</span>
                  {doc.file_size_bytes ? <span>{formatBytes(doc.file_size_bytes)}</span> : null}
                  {doc.file_mime_type && <span>{doc.file_mime_type.split('/').pop()}</span>}
                </div>
                {doc.description && <p className="text-xs text-gray-500 mt-0.5">{doc.description}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100">
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost" className="h-6 px-1"><ExternalLink className="h-3 w-3" /></Button></a>
                <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => openEdit(doc)}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="h-6 px-1 text-red-400" onClick={() => handleDelete(doc.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add document dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) { resetForm(); } setShowAdd(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
            <DialogDescription>Upload a file or link a document to this onboarding plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Upload mode toggle */}
            <div className="flex border rounded-md overflow-hidden text-sm w-fit">
              <button onClick={() => setUploadMode('file')} className={`flex items-center gap-1 px-3 py-1.5 ${uploadMode === 'file' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Upload className="h-3 w-3" />Upload File
              </button>
              <button onClick={() => setUploadMode('url')} className={`flex items-center gap-1 px-3 py-1.5 ${uploadMode === 'url' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Link2 className="h-3 w-3" />Link URL
              </button>
            </div>

            {/* File upload zone */}
            {uploadMode === 'file' && (
              <div
                onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  dragActive ? 'border-blue-400 bg-blue-50' : selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-green-700">{selectedFile.name}</p>
                      <p className="text-xs text-green-600">{formatBytes(selectedFile.size)} · {selectedFile.type || 'unknown type'}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 px-1" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}><X className="h-3 w-3" /></Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Drag and drop a file, or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, PowerPoint, images up to 50MB</p>
                  </div>
                )}
              </div>
            )}

            {/* URL input */}
            {uploadMode === 'url' && (
              <div>
                <Label>File URL</Label>
                <Input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className="mt-1" placeholder="https://drive.google.com/... or any download link" />
              </div>
            )}

            <div><Label>Document Name</Label><Input value={form.document_name} onChange={(e) => setForm({ ...form, document_name: e.target.value })} className="mt-1" placeholder="e.g., MSA - Acme Corp" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Document Type</Label>
                <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(d => (
                      <SelectItem key={d.value} value={d.value}>
                        <div><span>{d.label}</span></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-0.5">{DOC_TYPES.find(d => d.value === form.document_type)?.desc}</p>
              </div>
              <div>
                <Label>Link to Task (optional)</Label>
                <Select value={form.task_id} onValueChange={(v) => setForm({ ...form, task_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {tasks.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div><Label>Description (optional)</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || !canSave}>
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-1" />Add Document</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit document dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => { if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>Update the description, link, or details of this document.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Document Name</Label>
              <Input value={editForm.document_name} onChange={(e) => setEditForm({ ...editForm, document_name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>File URL</Label>
              <Input value={editForm.file_url} onChange={(e) => setEditForm({ ...editForm, file_url: e.target.value })} className="mt-1" placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Document Type</Label>
                <Select value={editForm.document_type} onValueChange={(v) => setEditForm({ ...editForm, document_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link to Task (optional)</Label>
                <Select value={editForm.task_id} onValueChange={(v) => setEditForm({ ...editForm, task_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {tasks.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving || !editForm.document_name.trim() || !editForm.file_url.trim()}>
              {editSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
